import {
  DateField as AriaDateField,
  DateFieldProps as AriaDateFieldBaseProps,
  DateInput as AriaDateInput,
  DateSegment as AriaDateSegment,
  DateValue,
  I18nProvider,
} from 'react-aria-components';
import { useObjectRef } from '@react-aria/utils';
import styles from './DateField.module.css';
import { Field, FieldProps } from '../_base/Field/Field';
import {
  useState,
  useMemo,
  forwardRef,
  Children,
  isValidElement,
  ReactNode,
  HTMLAttributes,
} from 'react';
import { parseDateValue } from '../../utils/date/date';
import { useDatePickerContext } from '../DatePicker/DatePickerContext';
import { FieldVariants } from '../../utils/forms/field';

type AriaDateFieldProps = Omit<
  AriaDateFieldBaseProps<DateValue>,
  | 'defaultValue'
  | 'value'
  | 'onChange'
  | 'minValue'
  | 'maxValue'
  | 'isDateUnavailable'
  | 'isDisabled'
  | 'isRequired'
  | 'isInvalid'
>;

// Extend the generic DateFieldProps<T> by specifying <T extends DateValue>
export type DateFieldProps = Omit<
  FieldProps<AriaDateFieldProps>,
  'maxWidthByChars' | 'granularity' | 'hourCycle' | 'hideTimeZone' | 'children'
> & {
  /**
   * Label for the date field
   */
  label?: string;
  /**
   * Description for the date field shown below the label
   */
  description?: string;
  /**
   * The default value of the date field.
   */
  defaultValue?: string | DateValue;
  /**
   * The controlled value of the date field.
   */
  value?: string | DateValue;
  /**
   * Function called when the date field value changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback function to mark cells as unavailable. Unavailable cells will have `aria-disabled="true"`
   */
  isDateUnavailable?: (value: string) => boolean;
  /**
   * Whether the date field is invalid, if `errorMessage` is set, it will be displayed
   */
  isInvalid?: boolean;
  isDisabled?: boolean;
  /**
   * Whether the date field is required. When `isRequired` is true, the '(optional)' text will not be displayed
   */
  isRequired?: boolean;
  /**
   * The minimum value of the date field.
   */
  minValue?: string | DateValue;
  /**
   * The maximum value of the date field.
   */
  maxValue?: string | DateValue;
  /**
   * The smallest time division that can be set in the date field.
   */
  granularity?: 'month' | 'day' | 'hour' | 'minute' | 'second';
  /**
   * When date field is showing hours, `hourCycle` controls whether a 12 or 24 hour clock is used.
   */
  hourCycle?: 12 | 24;
  /**
   * Hides the time zone abbreviation if visible
   */
  hideTimeZone?: boolean;
  /**
   * Children for DateField subcomponents
   */
  children?: React.ReactNode;
  /**
   * The translation for the optional text, used for localization
   * @default 'Optional'
   */
  optionalTranslation?: string;
  /**
   * Additional context for screen readers, rendered visually hidden next to the label.
   */
  visuallyHiddenLabel?: string;
  /**
   * The id of the error message element
   */
  errorId?: string;
  /**
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * The placement of the error message
   * @default 'top'
   */
  errorPlacement?: 'top' | 'bottom';
  /**
   * The number of columns the Field should have
   */
  columns?: FieldVariants['columns'];
  /**
   * Field width based on a rough estimate of the number of characters
   */
  maxWidthByChars?: FieldVariants['maxWidthByChars'];
  /**
   * Specify the date format and language. The same format will be shown for all users.
   */
  locale?: string;
};

const EndSlot = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  function EndSlot({ children }, ref) {
    return (
      <div className={styles['date-field__end-slot']} ref={ref}>
        {children}
      </div>
    );
  },
);

type DateFieldComponent = React.ForwardRefExoticComponent<
  DateFieldProps & React.RefAttributes<HTMLInputElement>
> & {
  EndSlot: typeof EndSlot;
};

interface DateFieldInputContainerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  children: ReactNode;
}

/**
 * Internal component to wrap AriaInput and apply border, background and padding styles
 */
export const DateFieldInputContainer = ({
  children,
  ...props
}: DateFieldInputContainerProps) => {
  return (
    <div {...props} className={styles['date-field__input-container']}>
      {children}
    </div>
  );
};

/**
 * Internal component to provide a scrollable wrapper to date segments
 */
export const DateFieldSegmentContainer = ({
  children,
}: {
  children: ReactNode;
}) => <div className={styles['date-field__segment-wrapper']}>{children}</div>;

export const DateField = forwardRef<HTMLDivElement, DateFieldProps>(
  function DateField(
    {
      defaultValue: defaultPropValue,
      value: controlledValue,
      onChange: onChangeProp,
      isDateUnavailable: isDateUnavailableProp,
      minValue: minPropValue,
      maxValue: maxPropValue,
      granularity = 'day',
      locale,
      children,
      ...props
    },
    forwardedRef,
  ) {
    const dateInputRef = useObjectRef(forwardedRef);
    const { isInDatePicker, isInDateRangePicker } = useDatePickerContext();

    const endSlotChildren = Children.toArray(children).find(
      (child) => isValidElement(child) && child.type === EndSlot,
    ) as React.ReactElement | undefined;

    const defaultValue = useMemo(
      () => parseDateValue(defaultPropValue),
      [defaultPropValue],
    );
    const controlledDateValue = useMemo(
      () => parseDateValue(controlledValue),
      [controlledValue],
    );
    const minValue = useMemo(
      () => parseDateValue(minPropValue),
      [minPropValue],
    );
    const maxValue = useMemo(
      () => parseDateValue(maxPropValue),
      [maxPropValue],
    );

    const [value, setValue] = useState<DateValue | null | undefined>(
      controlledDateValue ?? defaultValue,
    );

    const handleChange = (dateValue: DateValue | null) => {
      setValue(dateValue);
      if (!onChangeProp) return;
      /**
       * If the DateField is in the datepicker we need the 'dateValue' to be a DateValue type
       * otherwise it needs to be string type
       */
      if (isInDatePicker || isInDateRangePicker) {
        const onChange = onChangeProp as unknown as NonNullable<
          AriaDateFieldBaseProps<DateValue>['onChange']
        >;
        return onChange(dateValue);
      } else {
        const onChange = onChangeProp as NonNullable<
          DateFieldProps['onChange']
        >;
        return onChange(dateValue?.toDate('UTC').toISOString() || '');
      }
    };

    const handleIsDateUnavailable = (dateValue: DateValue) => {
      if (!isDateUnavailableProp) return false;
      /**
       * If the DateField is in the datepicker we need the 'dateValue' to be a DateValue type
       * otherwise it needs to be string type
       */
      if (isInDatePicker || isInDateRangePicker) {
        const isDateUnavailable =
          isDateUnavailableProp as unknown as NonNullable<
            AriaDateFieldBaseProps<DateValue>['isDateUnavailable']
          >;
        return isDateUnavailable(dateValue);
      } else {
        const isDateUnavailable = isDateUnavailableProp as NonNullable<
          DateFieldProps['isDateUnavailable']
        >;
        return isDateUnavailable(dateValue.toDate('UTC').toISOString());
      }
    };

    const dateInput = (
      <AriaDateInput ref={dateInputRef} className={styles['date-input']}>
        {(segment) => (
          <AriaDateSegment
            className={styles['date-segment']}
            segment={segment}
          />
        )}
      </AriaDateInput>
    );

    return (
      <I18nProvider locale={locale}>
        <Field<AriaDateFieldBaseProps<DateValue>>
          as={AriaDateField}
          data-testid="date-field"
          className={styles['date-field']}
          value={controlledDateValue ?? value}
          minValue={minValue}
          maxValue={maxValue}
          onChange={handleChange}
          isDateUnavailable={handleIsDateUnavailable}
          granularity={granularity as AriaDateFieldProps['granularity']}
          {...props}
        >
          {isInDateRangePicker ? (
            dateInput
          ) : (
            <DateFieldInputContainer>
              <DateFieldSegmentContainer>{dateInput}</DateFieldSegmentContainer>
              {endSlotChildren}
            </DateFieldInputContainer>
          )}
        </Field>
      </I18nProvider>
    );
  },
) as DateFieldComponent;

DateField.EndSlot = EndSlot;

export default DateField;
